/**
 * Product-intent SEO landing pages (#353, FR-15.7) — the four `/…-jeopardy…` surfaces that
 * win the create/play/multiplayer/customize search intents the homepage can't rank for
 * alone. Each page is a distinct content model behind ONE template ({@link ../pages/SeoLanding})
 * and ONE typed head ({@link ./route-seo}), so the pages stay visually consistent with `/`
 * and never duplicate each other (D353.5).
 *
 * "Jeopardy-style" copy + the bare-"Jeopardy" slugs are descriptive nominative use permitted
 * by FR-9.11 (D353.1); the product name reads from {@link BRAND_NAME} and every page carries
 * the {@link ./trademark}.SEO_DISCLAIMER non-affiliation note. All content is static (no
 * `window`/timers) so the pages prerender into crawlable HTML (FR-15.2/§16).
 */

import { BRAND_NAME } from '../ui/brand';
import { OG_IMAGE_PATH, PROD_ORIGIN, type DocumentHead } from './head-tags';

/** A three-part H1 so the middle token ("Jeopardy-Style") gets the amber highlight span. */
export interface SplitHeading {
  lead: string;
  amber: string;
  tail: string;
}

/** A named example topic-set shown on the customization page (issue §9). */
export interface ExampleSet {
  group: string;
  topics: string[];
}

/** The full content model for one product-intent landing page. */
export interface SeoLandingContent {
  /** Route path + self-canonical (leading `/`). */
  path: string;
  /** Small kicker above the H1. */
  eyebrow: string;
  /** The single, meaningful H1 (split for the amber highlight). */
  heading: SplitHeading;
  /** Hero lead paragraph. */
  lead: string;
  /** Primary CTA label (all target `/demo`, the zero-commitment create/join picker). */
  cta: string;
  /** Optional pill workflow strip (`.seo-flow`) — the user-facing steps. */
  flow?: string[];
  /** Optional example topic-sets (`.seo-examples`) — the customization page. */
  examples?: ExampleSet[];
  /** The explanatory section below the hero. */
  explain: { head: string; body: string };
  /** Short label used when OTHER pages cross-link to this one. */
  crossLinkLabel: string;
  /** `<title>` (brand interpolated). */
  title: string;
  /** Meta/OG description (unique per page). */
  description: string;
  /** `WebApplication` node description (accurate capabilities, Jeopardy-style framing). */
  appDescription: string;
}

/** The four pages, in the priority order the design doc lists them. */
export const SEO_LANDINGS: SeoLandingContent[] = [
  {
    path: '/online-jeopardy',
    eyebrow: 'Play online',
    heading: { lead: 'Play a ', amber: 'Jeopardy-Style', tail: ' Game Online' },
    lead: `Play a Jeopardy-style game online in your browser — no software to install. One host runs the board, everyone else joins from a phone, and you race a live buzzer together.`,
    cta: 'Create a Game',
    flow: [
      'Create or join a game',
      'The host opens the board',
      'Players join from any browser',
      'Buzz in and play',
    ],
    explain: {
      head: 'A full game show, right in the browser',
      body: `${BRAND_NAME} runs entirely online: the host opens a generated board on a laptop and can cast it to the TV, while players join from any phone or browser and buzz in live. Want to build the board yourself first? Use the game maker. Ready to play with a group? Jump straight into a multiplayer game.`,
    },
    crossLinkLabel: 'Play a Jeopardy-style game online',
    title: `Online Jeopardy-Style Game | Play ${BRAND_NAME}`,
    description: `Play a Jeopardy-style game online with ${BRAND_NAME} — host from your browser, players join and buzz in from their phones, no download. Create a game in minutes.`,
    appDescription: `${BRAND_NAME} is an online Jeopardy-style game you play in the browser: a host opens a generated board and players join and buzz in live from their phones.`,
  },
  {
    path: '/jeopardy-game-maker',
    eyebrow: 'Jeopardy game maker',
    heading: { lead: 'Create Your Own ', amber: 'Jeopardy-Style', tail: ' Game' },
    lead: `${BRAND_NAME} generates a complete, playable Jeopardy-style game from the topics you supply — categories, clues and all — then everyone plays it live with a buzzer.`,
    cta: 'Create Your Game',
    flow: [
      'Choose your topics',
      'Generate categories & questions',
      'Review & customize',
      'Start the game',
      'Players join',
      'Play the buzzer',
    ],
    explain: {
      head: 'From topics to a playable board in seconds',
      body: `Type in the topics your group cares about. ${BRAND_NAME} writes a full board of categories and clues on demand, you review and tweak anything you like, then share a link and play — no spreadsheets, no manual question-writing.`,
    },
    crossLinkLabel: 'Jeopardy game maker',
    title: `Jeopardy Game Maker – Create Custom Games Online | ${BRAND_NAME}`,
    description: `${BRAND_NAME} is a Jeopardy game maker: pick your topics and it generates a full, playable Jeopardy-style board of categories and clues. Create your game online.`,
    appDescription: `${BRAND_NAME} is a Jeopardy-style game maker that generates a complete, playable board of categories and clues from the topics a host supplies.`,
  },
  {
    path: '/multiplayer-jeopardy',
    eyebrow: 'Multiplayer',
    heading: {
      lead: 'Multiplayer ',
      amber: 'Jeopardy-Style',
      tail: ' Games with a Live Buzzer',
    },
    lead: `Play Jeopardy-style games with a whole group in real time. Every player's phone is a buzzer — the first to buzz in gets to answer — so a shared board turns into a live game show.`,
    cta: 'Start a Multiplayer Game',
    flow: [
      'Host opens the game',
      'Players open the join link',
      'Buzzing opens',
      'Players buzz',
      'First buzz wins',
    ],
    explain: {
      head: 'Phones as buzzers, no app to install',
      body: `The host opens the board on a laptop and can cast it to the TV; each player joins by link on their own phone and buzzes in live. It works great for a room full of people in person, and just as well for a remote group joining from anywhere — no downloads, no extra hardware.`,
    },
    crossLinkLabel: 'Multiplayer Jeopardy with a buzzer',
    title: `Multiplayer Jeopardy-Style Games with Live Buzzers | ${BRAND_NAME}`,
    description: `Play multiplayer Jeopardy-style games with ${BRAND_NAME} — players join from their phones and race a live buzzer, first buzz wins. No app download, in person or remote.`,
    appDescription: `${BRAND_NAME} is a multiplayer Jeopardy-style game with a live buzzer: players join from their phones and the first to buzz in answers, in real time.`,
  },
  {
    path: '/custom-jeopardy-games',
    eyebrow: 'Custom games',
    heading: {
      lead: 'Create a ',
      amber: 'Jeopardy-Style',
      tail: ' Game Around Any Topic',
    },
    lead: `Build a Jeopardy-style game around exactly the topics you want. Choose your own categories, let ${BRAND_NAME} generate the clues, and customize the board before you play.`,
    cta: 'Build a Custom Game',
    examples: [
      {
        group: 'Family Game Night',
        topics: ['Harry Potter', 'Space', '1980s Movies', 'Family History', 'Animals'],
      },
      {
        group: 'Classroom',
        topics: ['Ancient Rome', 'Fractions', 'Biology', 'Vocabulary', 'Geography'],
      },
      {
        group: 'Party',
        topics: [
          'The Birthday Person',
          'Music',
          'Movies',
          'Inside Jokes',
          'Travel',
          'Food',
        ],
      },
    ],
    explain: {
      head: 'Your topics, your board',
      body: `Nothing is pre-canned. Pick the topics that fit your group — a school subject, a franchise you love, your own family history — and ${BRAND_NAME} builds the categories and clues around them. The example sets below are just ideas; your board is whatever you make it.`,
    },
    crossLinkLabel: 'Custom games around your topics',
    title: `Custom Jeopardy-Style Games – Choose Your Own Topics | ${BRAND_NAME}`,
    description: `Create custom Jeopardy-style games with ${BRAND_NAME}: choose your own topics and categories, generate the clues, and customize the board before you play.`,
    appDescription: `${BRAND_NAME} builds custom Jeopardy-style games around any topics you choose: pick the categories, generate the clues, and customize the board before you play.`,
  },
];

/** Path → content lookup (the template resolves its content by route path). */
export const SEO_LANDING_BY_PATH: Record<string, SeoLandingContent> = Object.fromEntries(
  SEO_LANDINGS.map((c) => [c.path, c]),
);

/** The four landing paths, in order — consumed by the prerender/sitemap seam. */
export const SEO_LANDING_PATHS = SEO_LANDINGS.map((c) => c.path);

/** Absolute URL on the production host (breadcrumb `item` values must be absolute). */
function absUrl(path: string): string {
  return `${PROD_ORIGIN}${path}`;
}

/** The short breadcrumb name for a landing page (the H1 without the amber-split markup). */
function breadcrumbName(c: SeoLandingContent): string {
  return `${c.heading.lead}${c.heading.amber}${c.heading.tail}`.trim();
}

/**
 * Structured data for a landing page (#353, D353.6): a `WebApplication` node (accurate
 * capabilities only — no fabricated ratings/reviews/counts/prices/awards) plus a
 * `BreadcrumbList` (Home › this page) mirroring the site hierarchy.
 */
export function seoLandingJsonLd(c: SeoLandingContent): Record<string, unknown>[] {
  const webApp: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: BRAND_NAME,
    url: absUrl(c.path),
    description: c.appDescription,
    applicationCategory: 'GameApplication',
    operatingSystem: 'Any (web browser)',
    image: `${PROD_ORIGIN}${OG_IMAGE_PATH}`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
  const breadcrumb: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absUrl('/') },
      { '@type': 'ListItem', position: 2, name: breadcrumbName(c), item: absUrl(c.path) },
    ],
  };
  return [webApp, breadcrumb];
}

/** The typed head model for a landing page — self-canonical, OG image, JSON-LD graph. */
export function seoLandingHead(c: SeoLandingContent): DocumentHead {
  return {
    title: c.title,
    description: c.description,
    canonicalPath: c.path,
    image: OG_IMAGE_PATH,
    jsonLd: seoLandingJsonLd(c),
  };
}

/** All four heads keyed by path — merged into {@link ./route-seo}.ROUTE_HEADS. */
export const SEO_LANDING_HEADS: Record<string, DocumentHead> = Object.fromEntries(
  SEO_LANDINGS.map((c) => [c.path, seoLandingHead(c)]),
);
